import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "@tanstack/react-form";
import { Temporal } from "@js-temporal/polyfill";
import { z } from "zod";
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Field, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { TimeSlotPicker } from "@/components/vroom/time-slot-picker";
import { WorkorderPicker } from "@/components/vroom/workorder-picker";
import { CranePicker } from "@/components/vroom/crane-picker";
import { AdminWorkerPicker, type AdminWorker } from "./admin-worker-picker.tsx";
import { hoursEntriesCreate } from "@/api/generated/sdk.gen.ts";
import type { HoursEntriesCreateBody } from "@/api/generated/types.gen.ts";
import { dstStatus } from "@/lib/temporal";

interface LabelledOption {
  id: string;
  label: string;
}

type EntryType = "werk" | "onderhoud" | "geenwerk" | "verlof" | "ziek";

const insertSchema = z.object({
  worker: z.object({ id: z.string(), name: z.string() }).nullable(),
  type: z.enum(["werk", "onderhoud", "geenwerk", "verlof", "ziek"]),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Datum (YYYY-MM-DD) verplicht"),
  workorder: z.object({ id: z.string(), label: z.string() }).nullable(),
  crane: z.object({ id: z.string(), label: z.string() }).nullable(),
  startAt: z.string(),
  endAt: z.string(),
  crossMidnight: z.boolean(),
  note: z.string().optional(),
});

type FormValues = z.infer<typeof insertSchema>;

function composeEpochMs(dateISO: string, hhmm: string, addDays = 0): number {
  const [y, mo, d] = dateISO.split("-").map(Number);
  const [hh, mm] = hhmm.split(":").map(Number);
  const pdt = Temporal.PlainDateTime.from({
    year: y ?? 2026,
    month: mo ?? 1,
    day: d ?? 1,
    hour: hh ?? 0,
    minute: mm ?? 0,
  });
  const shifted = addDays === 0 ? pdt : pdt.add({ days: addDays });
  return shifted.toZonedDateTime("Europe/Amsterdam").toInstant().epochMilliseconds;
}

function todayISO(): string {
  return Temporal.Now.plainDateISO("Europe/Amsterdam").toString();
}

export interface UrenInsertSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultDate?: string;
}

export function UrenInsertSheet({ open, onOpenChange, defaultDate }: UrenInsertSheetProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: async (body: HoursEntriesCreateBody) => {
      const { data, error: e } = await hoursEntriesCreate({ body });
      if (e) throw e;
      if (!data) throw new Error("create failed");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["hours-entries"] }),
  });

  const initial: FormValues = {
    worker: null,
    type: "werk",
    date: defaultDate ?? todayISO(),
    workorder: null,
    crane: null,
    startAt: "07:00",
    endAt: "16:00",
    crossMidnight: false,
    note: "",
  };

  const form = useForm({
    defaultValues: initial,
    validators: {
      onSubmit: ({ value }: { value: FormValues }) => {
        const r = insertSchema.safeParse(value);
        if (!r.success) return r.error.issues[0]?.message ?? t("admin.uren.insert.errors.invalid");
        if (!value.worker) return t("admin.uren.insert.errors.worker-required");
        const needsWorkorder = value.type === "werk" || value.type === "onderhoud";
        if (needsWorkorder) {
          if (!value.workorder) return t("admin.uren.insert.errors.workorder-required");
          if (!value.crane) return t("admin.uren.insert.errors.crane-required");
        }
        if (!value.crossMidnight && value.endAt <= value.startAt) {
          return t("work.form.errors.endBeforeStart");
        }
        return undefined;
      },
    },
    onSubmit: async ({ value }: { value: FormValues }) => {
      setError(null);
      if (!value.worker) return;
      const startAt = composeEpochMs(value.date, value.startAt);
      const endAt = composeEpochMs(value.date, value.endAt, value.crossMidnight ? 1 : 0);
      try {
        await createMut.mutateAsync({
          userId: value.worker.id,
          type: value.type as EntryType,
          startAt,
          endAt,
          workorderId: value.workorder?.id ?? null,
          craneId: value.crane?.id ?? null,
          note: value.note?.trim() ? value.note.trim() : null,
          onBehalfOf: true,
        });
        toast.success(t("admin.uren.insert.toast.created"));
        onOpenChange(false);
        form.reset();
      } catch (err) {
        setError((err as Error).message);
      }
    },
  });

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="right">
      <DrawerContent className="max-w-md">
        <DrawerHeader>
          <DrawerTitle>{t("admin.uren.insert.title")}</DrawerTitle>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto px-4 pb-4">
          <FieldGroup>
            <form.Field name="worker">
              {(field) => (
                <Field data-invalid={field.state.meta.isTouched && !field.state.meta.isValid}>
                  <FieldLabel>{t("admin.uren.insert.worker")}</FieldLabel>
                  <AdminWorkerPicker
                    value={field.state.value as AdminWorker | null}
                    onChange={(v) => field.handleChange(v)}
                  />
                </Field>
              )}
            </form.Field>

            <form.Field name="type">
              {(field) => (
                <Field>
                  <FieldLabel>{t("admin.uren.insert.type")}</FieldLabel>
                  <ToggleGroup
                    type="single"
                    value={field.state.value as EntryType}
                    onValueChange={(v) => {
                      if (!v) return;
                      field.handleChange(v as EntryType);
                      if (v !== "werk" && v !== "onderhoud") {
                        form.setFieldValue("workorder", null);
                        form.setFieldValue("crane", null);
                      }
                    }}
                  >
                    <ToggleGroupItem value="werk">{t("work.type.werk")}</ToggleGroupItem>
                    <ToggleGroupItem value="onderhoud">{t("work.type.onderhoud")}</ToggleGroupItem>
                    <ToggleGroupItem value="geenwerk">{t("work.type.geenwerk")}</ToggleGroupItem>
                    <ToggleGroupItem value="verlof">{t("work.type.verlof")}</ToggleGroupItem>
                    <ToggleGroupItem value="ziek">{t("work.type.ziek")}</ToggleGroupItem>
                  </ToggleGroup>
                </Field>
              )}
            </form.Field>

            <form.Field name="date">
              {(field) => (
                <Field>
                  <FieldLabel htmlFor="uren-insert-date">{t("work.form.date.label")}</FieldLabel>
                  <Input
                    id="uren-insert-date"
                    type="date"
                    value={field.state.value as string}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </Field>
              )}
            </form.Field>

            <form.Subscribe selector={(s) => s.values.type}>
              {(type) =>
                type === "werk" || type === "onderhoud" ? (
                  <>
                    <form.Field name="workorder">
                      {(field) => (
                        <Field>
                          <FieldLabel>{t("work.form.workorder.label")}</FieldLabel>
                          <WorkorderPicker
                            value={field.state.value as LabelledOption | null}
                            onChange={(v) => {
                              field.handleChange(v);
                              form.setFieldValue("crane", null);
                            }}
                          />
                        </Field>
                      )}
                    </form.Field>

                    <form.Field name="crane">
                      {(craneField) => (
                        <form.Field name="workorder">
                          {(woField) => (
                            <Field>
                              <FieldLabel>{t("work.form.crane.label")}</FieldLabel>
                              <CranePicker
                                workorderId={
                                  (woField.state.value as LabelledOption | null)?.id ?? null
                                }
                                value={craneField.state.value as LabelledOption | null}
                                onChange={(v) => craneField.handleChange(v)}
                              />
                            </Field>
                          )}
                        </form.Field>
                      )}
                    </form.Field>
                  </>
                ) : null
              }
            </form.Subscribe>

            <form.Subscribe
              selector={(s) => ({
                date: s.values.date,
                start: s.values.startAt,
                end: s.values.endAt,
                crossMidnight: s.values.crossMidnight,
              })}
            >
              {({ date, start, end, crossMidnight }) => {
                const [dy, dmo, dd] = date.split("-").map(Number);
                return (
                  <Field>
                    <FieldLabel>{t("work.form.timeSlot.label")}</FieldLabel>
                    <TimeSlotPicker
                      value={{ start, end, crossMidnight }}
                      onChange={(v) => {
                        form.setFieldValue("startAt", v.start);
                        form.setFieldValue("endAt", v.end);
                        form.setFieldValue("crossMidnight", v.crossMidnight);
                      }}
                      dstStatusFor={(h, m) => dstStatus(dy ?? 2026, dmo ?? 1, dd ?? 1, h, m)}
                    />
                  </Field>
                );
              }}
            </form.Subscribe>

            <form.Field name="note">
              {(field) => (
                <Field>
                  <FieldLabel htmlFor="uren-insert-note">{t("work.form.note.label")}</FieldLabel>
                  <Textarea
                    id="uren-insert-note"
                    value={(field.state.value as string | undefined) ?? ""}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder={t("work.form.note.placeholder")}
                  />
                </Field>
              )}
            </form.Field>

            <form.Subscribe selector={(s) => s.errors}>
              {(errors) =>
                errors.length > 0 ? (
                  <FieldError
                    errors={errors.flatMap((e) =>
                      typeof e === "string" ? [{ message: e }] : Array.isArray(e) ? e : [e],
                    )}
                  />
                ) : null
              }
            </form.Subscribe>
            {error && <FieldError errors={[{ message: error }]} />}
          </FieldGroup>
        </div>

        <DrawerFooter className="flex-row justify-end gap-2">
          <Button
            type="button"
            variant="default"
            disabled={createMut.isPending}
            onClick={() => onOpenChange(false)}
          >
            {t("common.buttons.cancel.label")}
          </Button>
          <Button
            type="button"
            variant="primary"
            loading={createMut.isPending}
            disabled={createMut.isPending}
            onClick={() => void form.handleSubmit()}
          >
            {t("admin.uren.insert.submit")}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
